'use client';

import { usePulseTracker } from '@cineamo/pulse/src/react/PulseProvider';
import { trackedEntity } from '@cineamo/pulse/src/subject';
import classnames from 'classnames';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';

import { NoOnlineTicketUrlModal } from '@/components/NoOnlineTicketUrlModal';

import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { Client } from '@cineamo/api/constants/Client.enum';
import { PurchaseReasonEnum } from '@cineamo/api/constants/PurchaseReason.enum';

import { getOnlineTicketingLinkFlag } from '@/helpers/feature-flags';
import { appendReservationShowingId } from '@/helpers/reservation-ticket-url-helper';
import { onTicketButtonClick } from '@/helpers/ticket-purchase-intent-helper';
import { isDatetimePast } from '@/helpers/time-helper';
import { getHoursUntilShowing, getOnlineTicketUrl } from '@cineamo/ui/src/helpers/showing-helper';

import { CachedContentData } from '@/hooks/useCachedContentData';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { usePageName } from '@/utils/tracking-helper';

export function useTicketUrl(showing: ShowingDto) {
    const searchParams = useSearchParams();
    const domainConfiguration = useDomainConfiguration();
    const [useNativeOnlineTicketingFlag] = useState<boolean>(() => getOnlineTicketingLinkFlag(searchParams.toString()));

    const resolvedTicketUrl = useNativeOnlineTicketingFlag
        ? `/booking/${showing.id}`
        : getOnlineTicketUrl(showing, Client.CinemaWebsite);
    // Preselect the showing when the ticket link points to this cinema's own /reservation page.
    const ticketUrl = appendReservationShowingId(
        resolvedTicketUrl ?? undefined,
        showing.id,
        domainConfiguration.domain
    );
    const hasTicketUrl = (ticketUrl?.length ?? 0) > 0;
    const isExternal = hasTicketUrl && !ticketUrl?.startsWith('/');
    const target = isExternal ? '_blank' : '_self';

    return { ticketUrl, hasTicketUrl, isExternal, target };
}

type TicketLinkProps = Readonly<{
    showing: ShowingDto;
    content?: CachedContentData;
    children: React.ReactNode;
    className?: string;
    onClick?: (e: React.MouseEvent) => void;
}>;

export default function TicketLink({
    showing,
    content,
    children,
    className,
    onClick
}: TicketLinkProps): React.JSX.Element {
    const domainConfiguration = useDomainConfiguration();
    const t = useTranslations();
    const { track: pulseTrack } = usePulseTracker();
    const { track } = useAnalytics();
    const currentPageName = usePageName();

    const { ticketUrl, hasTicketUrl, isExternal } = useTicketUrl(showing);
    const isDisabled = isDatetimePast(showing.startDatetime, true);
    const [noTicketUrlIsOpen, setNoTicketUrlIsOpen] = useState(false);

    function handleClick(e: React.MouseEvent) {
        onClick?.(e);
        if (e.defaultPrevented) return;

        pulseTrack({
            ...trackedEntity({ movieId: content?.movieId, contentId: showing.contentId }),
            event_type: 'ticket_purchase_intent',
            context: {
                showing_id: showing.id,
                ...(showing.showingTagIds?.length && { showing_tag_ids: showing.showingTagIds }),
                page: currentPageName
            }
        });

        if (!hasTicketUrl) {
            setNoTicketUrlIsOpen(true);
            return;
        }

        if (isDisabled) {
            return;
        }

        onTicketButtonClick(showing.id, PurchaseReasonEnum.CINEMA_WEBSITE, showing.contentId);
        track('Ticket Button Clicked', {
            page: currentPageName,
            showingId: showing.id,
            showingName: showing.name || content?.title || 'N/A',
            contentId: showing.contentId,
            contentName: content?.name || 'N/A',
            hoursUntilShowing: getHoursUntilShowing(showing.startDatetime) || 'N/A'
        });
    }

    return (
        <>
            {hasTicketUrl && !isDisabled ? (
                <a
                    href={ticketUrl}
                    target={isExternal ? '_blank' : '_self'}
                    rel={isExternal ? 'nofollow noopener noreferrer' : undefined}
                    aria-label={t('tickets.tickets')}
                    className={className}
                    onClick={handleClick}>
                    {children}
                </a>
            ) : (
                <button
                    onClick={handleClick}
                    aria-label={t('tickets.tickets')}
                    className={classnames(
                        'appearance-none border-0 bg-transparent p-0 m-0 text-left text-inherit w-full',
                        className
                    )}>
                    {children}
                </button>
            )}
            {noTicketUrlIsOpen && (
                <NoOnlineTicketUrlModal
                    domainConfiguration={domainConfiguration}
                    modalIsOpen={noTicketUrlIsOpen}
                    setModalIsOpen={setNoTicketUrlIsOpen}
                />
            )}
        </>
    );
}
